import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, DatePicker, Button, Statistic, Table, Tag, Space, Row, Col, Typography, Spin } from 'antd';
import { 
  UserOutlined, 
  CalendarOutlined, 
  FilePdfOutlined,
  FileExcelOutlined,
  PrinterOutlined,
  DollarOutlined,
  TeamOutlined,
  ShoppingOutlined,
  TransactionOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import './CashierSummaries.css';
import fetchData from '../../functions/fetchData';
import Layout from '../../layout/Layout';
import Header from '../../components/Header';
import FloatingNavModal from '../../components/floatingbuttons/FloatingNavModal';
import { getLastCloseDate } from '../../api/dayCloseApi';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { ToastContainer, toast } from 'react-toastify';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const CashierSummaries = () => {
  const navigate = useNavigate();
  
  const [selectedDate, setSelectedDate] = useState('');
  const [summaryData, setSummaryData] = useState({
    totalSales: 0,
    totalSessions: 0,
    totalTransactions: 0,
    itemsSold: 0,
    sessions: []
  });
  const [loading, setLoading] = useState(false);
  const [dateLoading, setDateLoading] = useState(true);

  // Get user information for authentication
  const userType = (localStorage.getItem('usertype') || sessionStorage.getItem('usertype') || '').toLowerCase();
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  
  const isAuthenticated = !!token;
  const isAuthorized = userType === 'admin' || userType === 'account';

  // Fetch default date from API
  const fetchDefaultDate = useCallback(async () => {
    setDateLoading(true);
    try {
      const response = await getLastCloseDate();
      if (response && response.currentBillingDate) {
        setSelectedDate(response.currentBillingDate);
      } else {
        // Fallback to current date if API fails
        setSelectedDate(new Date().toISOString().split('T')[0]);
      }
    } catch (error) {
      console.error('Error fetching default date:', error);
      // Fallback to current date if API fails
      setSelectedDate(new Date().toISOString().split('T')[0]);
    } finally {
      setDateLoading(false);
    }
  }, []);

  // Fetch cashier summaries data
  const fetchCashierSummaries = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch from cashier_summaries table for the selected date
      const whereCondition = {
        date: selectedDate
      };
      
      const summariesData = await fetchData('cashier_summaries', null, 'id', whereCondition);
      
      if (summariesData && summariesData.length > 0) {
        let totalSales = 0;
        let totalTransactions = 0;
        let totalSessions = summariesData.length;
        let itemsSold = 0;
        
        const processedSessions = summariesData.map((session, index) => {
          const sales = parseFloat(session.total_sales) || 0;
          const transactions = parseInt(session.total_transactions) || 0;
          const cash = parseFloat(session.cash_amount) || 0;
          const upi = parseFloat(session.upi_amount) || 0;
          const card = parseFloat(session.card_amount) || 0;
          const qr = parseFloat(session.qr_amount) || 0;
          const nonCash = parseFloat(session.non_cash_amount) || 0;
          const items = parseInt(session.items_sold) || 0;
          
          totalSales += sales;
          totalTransactions += transactions;
          itemsSold += items;
          
          return {
            sno: index + 1,
            cashierName: session.cashier_name || 'Unknown',
            cashierId: session.cashier_id || '',
            startTime: session.start_time || 'N/A',
            endTime: session.end_time || 'N/A',
            duration: session.duration || 'N/A',
            totalSales: sales,
            transactions: transactions,
            cash: cash,
            upi: upi,
            card: card,
            qr: qr,
            nonCash: nonCash,
            items: items,
            status: session.status || 'CLOSED',
            date: session.date
          };
        });
        
        setSummaryData({
          totalSales,
          totalSessions,
          totalTransactions,
          itemsSold,
          sessions: processedSessions
        });
      } else {
        setSummaryData({
          totalSales: 0,
          totalSessions: 0,
          totalTransactions: 0,
          itemsSold: 0,
          sessions: []
        });
      }
    } catch (error) {
      console.error('Error fetching cashier summaries:', error);
      toast.error('Failed to fetch cashier summaries data');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  // Export to PDF function
  const exportToPDF = () => {
    const doc = new jsPDF('landscape');
    
    // Add title
    doc.setFontSize(20);
    doc.text('Cashier Summary Report', 20, 20);
    
    // Add date and summary info
    doc.setFontSize(12);
    doc.text(`Date: ${new Date(selectedDate).toLocaleDateString()}`, 20, 40);
    doc.text(`Total Sessions: ${summaryData.totalSessions}`, 20, 50);
    doc.text(`Total Sales: ₹${summaryData.totalSales.toLocaleString()}`, 20, 60);
    doc.text(`Total Transactions: ${summaryData.totalTransactions}`, 20, 70);
    
    // Prepare data for table
    const tableData = summaryData.sessions.map((session) => [
      session.sno,
      session.cashierName,
      session.startTime,
      session.endTime,
      session.duration,
      `₹${session.totalSales.toLocaleString()}`,
      session.transactions,
      `₹${session.cash.toLocaleString()}`,
      `₹${session.upi.toLocaleString()}`,
      `₹${session.card.toLocaleString()}`,
      `₹${session.qr.toLocaleString()}`,
      `₹${session.nonCash.toLocaleString()}`,
      session.items,
      session.status
    ]);
    
    // Add totals row
    tableData.push([
      'TOTALS:',
      `${summaryData.totalSessions} Sessions`,
      '',
      '',
      '',
      `₹${summaryData.totalSales.toLocaleString()}`,
      summaryData.totalTransactions,
      `₹${summaryData.sessions.reduce((sum, s) => sum + s.cash, 0).toLocaleString()}`,
      `₹${summaryData.sessions.reduce((sum, s) => sum + s.upi, 0).toLocaleString()}`,
      `₹${summaryData.sessions.reduce((sum, s) => sum + s.card, 0).toLocaleString()}`,
      `₹${summaryData.sessions.reduce((sum, s) => sum + s.qr, 0).toLocaleString()}`,
      `₹${summaryData.sessions.reduce((sum, s) => sum + s.nonCash, 0).toLocaleString()}`,
      summaryData.itemsSold,
      ''
    ]);
    
    // Add table
    doc.autoTable({
      startY: 90,
      head: [['S.NO', 'CASHIER NAME', 'START TIME', 'END TIME', 'DURATION', 'TOTAL SALES', 'TRANSACTIONS', 'CASH', 'UPI', 'CARD', 'QR', 'NON-CASH', 'ITEMS', 'STATUS']],
      body: tableData,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [52, 58, 64] },
      footStyles: { fillColor: [108, 117, 125], fontStyle: 'bold' },
      margin: { top: 90 }
    });
    
    doc.save(`cashier-summaries-${selectedDate}.pdf`);
    toast.success('PDF exported successfully!');
  };
  
  // Export to Excel function
  const exportToExcel = () => {
    const worksheetData = [
      ['Cashier Summary Report'],
      [`Date: ${new Date(selectedDate).toLocaleDateString()}`],
      [`Total Sessions: ${summaryData.totalSessions}`],
      [`Total Sales: ₹${summaryData.totalSales.toLocaleString()}`],
      [`Total Transactions: ${summaryData.totalTransactions}`],
      [],
      ['S.NO', 'CASHIER NAME', 'START TIME', 'END TIME', 'DURATION', 'TOTAL SALES', 'TRANSACTIONS', 'CASH', 'UPI', 'CARD', 'QR', 'NON-CASH', 'ITEMS', 'STATUS'],
      ...summaryData.sessions.map((session) => [
        session.sno,
        session.cashierName,
        session.startTime,
        session.endTime,
        session.duration,
        session.totalSales,
        session.transactions,
        session.cash,
        session.upi,
        session.card,
        session.qr,
        session.nonCash,
        session.items,
        session.status
      ]),
      [],
      ['TOTALS:', `${summaryData.totalSessions} Sessions`, '', '', '', summaryData.totalSales, summaryData.totalTransactions,
       summaryData.sessions.reduce((sum, s) => sum + s.cash, 0),
       summaryData.sessions.reduce((sum, s) => sum + s.upi, 0),
       summaryData.sessions.reduce((sum, s) => sum + s.card, 0),
       summaryData.sessions.reduce((sum, s) => sum + s.qr, 0),
       summaryData.sessions.reduce((sum, s) => sum + s.nonCash, 0),
       summaryData.itemsSold, '']
    ];
    
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Cashier Summaries');
    
    XLSX.writeFile(workbook, `cashier-summaries-${selectedDate}.xlsx`);
    toast.success('Excel exported successfully!');
  };

  // Generate Thermal Print
  const generateThermalPrint = () => {
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    const printContent = `
      <html>
        <head>
          <title>Cashier Summaries - ${selectedDate}</title>
          <style>
            body { font-family: 'Courier New', monospace; font-size: 12px; margin: 20px; }
            .header { text-align: center; font-weight: bold; margin-bottom: 20px; }
            .summary { margin-bottom: 20px; }
            .summary div { margin: 5px 0; }
            table { width: 100%; border-collapse: collapse; font-size: 10px; }
            th, td { border: 1px solid #000; padding: 4px; text-align: left; }
            th { background-color: #f0f0f0; font-weight: bold; }
            .totals { background-color: #e0e0e0; font-weight: bold; }
          </style>
        </head>
        <body>
          <div class="header">
            <h2>CASHIER SUMMARY REPORT</h2>
            <div>Date: ${new Date(selectedDate).toLocaleDateString()}</div>
          </div>
          
          <div class="summary">
            <div>Total Sessions: ${summaryData.totalSessions}</div>
            <div>Total Sales: ₹${summaryData.totalSales.toLocaleString()}</div>
            <div>Total Transactions: ${summaryData.totalTransactions}</div>
            <div>Items Sold: ${summaryData.itemsSold}</div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>S.NO</th>
                <th>CASHIER</th>
                <th>START</th>
                <th>END</th>
                <th>SALES</th>
                <th>TRANS</th>
                <th>STATUS</th>
              </tr>
            </thead>
            <tbody>
              ${summaryData.sessions.map(session => `
                <tr>
                  <td>${session.sno}</td>
                  <td>${session.cashierName}</td>
                  <td>${session.startTime}</td>
                  <td>${session.endTime}</td>
                  <td>₹${session.totalSales.toLocaleString()}</td>
                  <td>${session.transactions}</td>
                  <td>${session.status}</td>
                </tr>
              `).join('')}
              <tr class="totals">
                <td colspan="4">TOTALS:</td>
                <td>₹${summaryData.totalSales.toLocaleString()}</td>
                <td>${summaryData.totalTransactions}</td>
                <td>${summaryData.totalSessions} Sessions</td>
              </tr>
            </tbody>
          </table>
        </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
    toast.success('Print initiated successfully!');
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (!isAuthorized) {
      toast.error('Access denied. Only admin and account users can access this page.');
      navigate('/dashboard/admin');
      return;
    }

    // Fetch default date first
    fetchDefaultDate();
  }, [isAuthenticated, isAuthorized, navigate, fetchDefaultDate]);

  // Fetch cashier summaries when date is available
  useEffect(() => {
    if (selectedDate && !dateLoading) {
      fetchCashierSummaries();
    }
  }, [selectedDate, dateLoading, fetchCashierSummaries]);

  if (!isAuthenticated || !isAuthorized) {
    return null;
  }

  return (
    <Layout>
      <Header title="Cashier Summary Report" />
      <ToastContainer />
      
      <div className="cashier-summaries" style={{ padding: '24px', background: '#f5f7fa' }}>
        {/* Header Card with Soft Gradient */}
        <Card 
          style={{
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #4881b6ff 0%, #2f6152ff 100%)',
            border: 'none',
            marginBottom: '24px',
            boxShadow: '0 10px 30px rgba(102, 126, 234, 0.2)'
          }}
          bodyStyle={{ padding: '24px' }}
        >
          <Row justify="space-between" align="middle" gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Space direction="vertical" size={4}>
                <Title level={3} style={{ color: '#fff', margin: 0 }}>
                  <UserOutlined style={{ marginRight: '8px' }} />
                  Cashier Summary Report
                </Title>
                <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px' }}>
                  View cashier session summaries and export reports
                </Text>
              </Space>
            </Col>
            
            <Col xs={24} md={12}>
              <Space wrap style={{ float: 'right' }}>
                <DatePicker
                  value={selectedDate ? dayjs(selectedDate) : null}
                  onChange={(date) => setSelectedDate(date ? date.format('YYYY-MM-DD') : '')}
                  disabled={dateLoading}
                  style={{ width: '200px' }}
                  format="YYYY-MM-DD"
                  suffixIcon={<CalendarOutlined style={{ color: '#667eea' }} />}
                />
                
                <Button 
                  type="primary"
                  danger
                  icon={<FilePdfOutlined />}
                  onClick={exportToPDF}
                  disabled={summaryData.sessions.length === 0}
                >
                  PDF
                </Button>
                
                <Button 
                  type="primary"
                  style={{ background: '#10b981', borderColor: '#10b981' }}
                  icon={<FileExcelOutlined />}
                  onClick={exportToExcel}
                  disabled={summaryData.sessions.length === 0}
                >
                  EXCEL
                </Button>
                
                <Button 
                  type="primary"
                  icon={<PrinterOutlined />}
                  onClick={generateThermalPrint}
                  disabled={summaryData.sessions.length === 0}
                >
                  PRINT
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <Spin size="large" indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
            <p style={{ marginTop: '16px', color: '#667eea', fontSize: '16px' }}>Loading cashier summaries...</p>
          </div>
        ) : (
          <>
            {/* Date Banner */}
            <Card 
              style={{
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                border: 'none',
                marginBottom: '24px',
                boxShadow: '0 4px 15px rgba(240, 147, 251, 0.2)'
              }}
              bodyStyle={{ padding: '16px 24px' }}
            >
              <Space>
                <CalendarOutlined style={{ fontSize: '24px', color: '#fff' }} />
                <div>
                  <Text style={{ color: '#fff', fontSize: '18px', fontWeight: '600' }}>
                    {new Date(selectedDate).toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </Text>
                  <div>
                    <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px' }}>
                      {summaryData.totalSessions} Sessions
                    </Text>
                  </div>
                </div>
              </Space>
            </Card>

            {/* Overview Cards with Soft Gradients */}
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
              <Col xs={24} sm={12} lg={6}>
                <Card 
                  style={{
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, #d4fc79 0%, #96e6a1 100%)',
                    border: 'none',
                    boxShadow: '0 8px 20px rgba(150, 230, 161, 0.3)'
                  }}
                  bodyStyle={{ padding: '24px' }}
                >
                  <Statistic
                    title={<Text style={{ color: '#166534', fontSize: '14px', fontWeight: '600' }}>Total Sales</Text>}
                    value={summaryData.totalSales}
                    precision={0}
                    prefix="฿"
                    valueStyle={{ color: '#166534', fontWeight: '700', fontSize: '32px' }}
                    suffix={<DollarOutlined style={{ fontSize: '24px', marginLeft: '8px', color: '#166534' }} />}
                  />
                </Card>
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <Card 
                  style={{
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%)',
                    border: 'none',
                    boxShadow: '0 8px 20px rgba(251, 194, 235, 0.3)'
                  }}
                  bodyStyle={{ padding: '24px' }}
                >
                  <Statistic
                    title={<Text style={{ color: '#7c3aed', fontSize: '14px', fontWeight: '600' }}>Total Sessions</Text>}
                    value={summaryData.totalSessions}
                    valueStyle={{ color: '#7c3aed', fontWeight: '700', fontSize: '32px' }}
                    suffix={<TeamOutlined style={{ fontSize: '24px', marginLeft: '8px', color: '#7c3aed' }} />}
                  />
                </Card>
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <Card 
                  style={{
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)',
                    border: 'none',
                    boxShadow: '0 8px 20px rgba(132, 250, 176, 0.3)'
                  }}
                  bodyStyle={{ padding: '24px' }}
                >
                  <Statistic
                    title={<Text style={{ color: '#0369a1', fontSize: '14px', fontWeight: '600' }}>Total Transactions</Text>}
                    value={summaryData.totalTransactions}
                    valueStyle={{ color: '#0369a1', fontWeight: '700', fontSize: '32px' }}
                    suffix={<TransactionOutlined style={{ fontSize: '24px', marginLeft: '8px', color: '#0369a1' }} />}
                  />
                </Card>
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <Card 
                  style={{
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, #fdcbf1 0%, #fdcbf1 1%, #e6dee9 100%)',
                    border: 'none',
                    boxShadow: '0 8px 20px rgba(253, 203, 241, 0.3)'
                  }}
                  bodyStyle={{ padding: '24px' }}
                >
                  <Statistic
                    title={<Text style={{ color: '#a21caf', fontSize: '14px', fontWeight: '600' }}>Items Sold</Text>}
                    value={summaryData.itemsSold}
                    valueStyle={{ color: '#a21caf', fontWeight: '700', fontSize: '32px' }}
                    suffix={<ShoppingOutlined style={{ fontSize: '24px', marginLeft: '8px', color: '#a21caf' }} />}
                  />
                </Card>
              </Col>
            </Row>

            {/* Sessions Table */}
            <Card
              style={{
                borderRadius: '16px',
                border: 'none',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
              }}
              bodyStyle={{ padding: 0 }}
            >
              {summaryData.sessions.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                  <Text type="secondary" style={{ fontSize: '16px' }}>
                    No cashier sessions found for {new Date(selectedDate).toLocaleDateString()}
                  </Text>
                </div>
              ) : (
                <Table
                  dataSource={summaryData.sessions}
                  pagination={false}
                  scroll={{ x: 1400 }}
                  rowKey="sno"
                  columns={[
                    {
                      title: 'S.NO',
                      dataIndex: 'sno',
                      key: 'sno',
                      width: 60,
                      fixed: 'left'
                    },
                    {
                      title: 'CASHIER NAME',
                      dataIndex: 'cashierName',
                      key: 'cashierName',
                      width: 180,
                      fixed: 'left',
                      render: (text, record) => (
                        <div>
                          <Text strong>{text}</Text>
                          {record.cashierId && (
                            <div><Text type="secondary" style={{ fontSize: '12px' }}>ID: {record.cashierId}</Text></div>
                          )}
                        </div>
                      )
                    },
                    {
                      title: 'START TIME',
                      dataIndex: 'startTime',
                      key: 'startTime',
                      width: 100
                    },
                    {
                      title: 'END TIME',
                      dataIndex: 'endTime',
                      key: 'endTime',
                      width: 100
                    },
                    {
                      title: 'DURATION',
                      dataIndex: 'duration',
                      key: 'duration',
                      width: 100
                    },
                    {
                      title: 'TOTAL SALES',
                      dataIndex: 'totalSales',
                      key: 'totalSales',
                      width: 120,
                      render: (value) => (
                        <Text strong style={{ color: '#10b981' }}>฿ {value.toLocaleString()}</Text>
                      )
                    },
                    {
                      title: 'TRANSACTIONS',
                      dataIndex: 'transactions',
                      key: 'transactions',
                      width: 120,
                      render: (value) => <Tag color="blue">{value}</Tag>
                    },
                    {
                      title: 'CASH',
                      dataIndex: 'cash',
                      key: 'cash',
                      width: 100,
                      render: (value) => <Text style={{ color: '#10b981' }}>฿ {value.toLocaleString()}</Text>
                    },
                    {
                      title: 'UPI',
                      dataIndex: 'upi',
                      key: 'upi',
                      width: 100,
                      render: (value) => <Text style={{ color: '#3b82f6' }}>฿ {value.toLocaleString()}</Text>
                    },
                    {
                      title: 'CARD',
                      dataIndex: 'card',
                      key: 'card',
                      width: 100,
                      render: (value) => <Text style={{ color: '#f59e0b' }}>฿ {value.toLocaleString()}</Text>
                    },
                    {
                      title: 'QR',
                      dataIndex: 'qr',
                      key: 'qr',
                      width: 100,
                      render: (value) => <Text style={{ color: '#06b6d4' }}>฿ {value.toLocaleString()}</Text>
                    },
                    {
                      title: 'NON-CASH',
                      dataIndex: 'nonCash',
                      key: 'nonCash',
                      width: 100,
                      render: (value) => <Text type="secondary">฿ {value.toLocaleString()}</Text>
                    },
                    {
                      title: 'ITEMS',
                      dataIndex: 'items',
                      key: 'items',
                      width: 80,
                      render: (value) => <Tag color="default">{value}</Tag>
                    },
                    {
                      title: 'STATUS',
                      dataIndex: 'status',
                      key: 'status',
                      width: 100,
                      fixed: 'right',
                      render: (value) => (
                        <Tag color={value === 'CLOSED' ? 'success' : 'warning'}>
                          {value}
                        </Tag>
                      )
                    }
                  ]}
                  summary={() => (
                    <Table.Summary fixed>
                      <Table.Summary.Row style={{ background: '#f3f4f6', fontWeight: 'bold' }}>
                        <Table.Summary.Cell index={0} colSpan={5}>
                          <Text strong style={{ fontSize: '14px' }}>TOTALS:</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={5}>
                          <Text strong style={{ color: '#10b981', fontSize: '14px' }}>
                            ฿ {summaryData.totalSales.toLocaleString()}
                          </Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={6}>
                          <Text strong>{summaryData.totalTransactions}</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={7}>
                          <Text strong>฿ {summaryData.sessions.reduce((sum, s) => sum + s.cash, 0).toLocaleString()}</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={8}>
                          <Text strong>฿ {summaryData.sessions.reduce((sum, s) => sum + s.upi, 0).toLocaleString()}</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={9}>
                          <Text strong>฿ {summaryData.sessions.reduce((sum, s) => sum + s.card, 0).toLocaleString()}</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={10}>
                          <Text strong>฿ {summaryData.sessions.reduce((sum, s) => sum + s.qr, 0).toLocaleString()}</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={11}>
                          <Text strong>฿ {summaryData.sessions.reduce((sum, s) => sum + s.nonCash, 0).toLocaleString()}</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={12}>
                          <Text strong>{summaryData.itemsSold}</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={13}>
                          <Text strong>{summaryData.totalSessions} Sessions</Text>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  )}
                />
              )}
            </Card>
          </>
        )}

        {/* Floating Navigation Modal */}
        <FloatingNavModal currentPage="cashier-summaries" />
      </div>
    </Layout>
  );
};

export default CashierSummaries;